import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { get, isEqual, omit } from 'lodash'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, useMediaQuery, useTheme, Autocomplete } from '@mui/material'
import { AuthPersonsSelectType, ControlledTextFieldProps, OptionType } from '../../../types'
import { filterOptionsAny, filterOptionsStart, handleLatinKeysDown } from '../../../utils'
import { Maybe } from '../../../graphql'
import { StyledCheckbox } from '../StyledComponents'
import { TextFieldProps } from '@mui/material/TextField'
import { MobileSelector } from './MobileSelector'
import { StyledTextField } from './StyledComponents'

interface FormAutocompleteSelectProps {
  name: string
  /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/
  data: Array<any> | AuthPersonsSelectType[]
  disableClearable?: boolean
  readOnly?: boolean
  defaultValue?: Maybe<string> | undefined
  filterOptionPosition?: 'start' | 'any'
  setDisableParentScroll?: React.Dispatch<React.SetStateAction<boolean>>
  onInputChange?: (optionValue: string | null) => void
  disablePortal?: boolean
  isParentScroll?: boolean
  onlyLatinLetters?: boolean
  doubleRows?: boolean
  isDynamicLabels?: boolean
  endAdornmentIcon?: React.ReactNode
}

const FormAutocompleteSelectComponent: React.FC<
  FormAutocompleteSelectProps & Partial<TextFieldProps & ControlledTextFieldProps>
> = ({
  name = '',
  label,
  disableClearable,
  data,
  shouldValidateParam,
  readOnly = false,
  defaultValue,
  rules,
  filterOptionPosition = 'start',
  setDisableParentScroll,
  onInputChange,
  withCheckbox = false,
  isParentScroll,
  disablePortal,
  onlyLatinLetters = true,
  doubleRows,
  isDynamicLabels,
  endAdornmentIcon,
  ...rest
}) => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const { errors, register, setValue, getValues, watch, clearErrors } = useFormContext()
  const error = get(errors, name || 'FormAutocompleteSelect', null)
  const value = watch(name)
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const selectedOption = useMemo(() => data.find(({ key }) => key === value), [value, data])

  const isLongLabel = typeof label === 'string' && label.length > 30
  const filterOption = filterOptionPosition === 'start' ? filterOptionsStart : filterOptionsAny

  const handleChange = (selectedValue: string | null) => {
    setValue(name, selectedValue || null, {
      shouldValidate: shouldValidateParam ?? true,
      shouldDirty: true,
    })
    onInputChange && onInputChange(selectedValue)
    clearErrors(name)
  }

  const handleInputChange = useCallback(
    (_, value: string, reason: string) => {
      if (reason === 'input') {
        setInputValue(value)
        if (!value) {
          setValue(name, null, { shouldValidate: true })
        }
      }
    },
    [name, setValue],
  )

  const handleOpen = useCallback(() => {
    if (!readOnly) {
      setIsOpen(true)
      setDisableParentScroll?.(true)
    }
  }, [readOnly, setDisableParentScroll])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setDisableParentScroll?.(false)
  }, [setDisableParentScroll])

  useEffect(() => {
    if (selectedOption) {
      setInputValue(selectedOption.label)
    } else {
      setInputValue('')
    }
  }, [selectedOption])

  useEffect(() => {
    if (!getValues(name)) {
      register(name, { ...(rules ?? {}) })
    }
    if (defaultValue) {
      setValue(name, defaultValue)
    }
  }, [register, name, rules, defaultValue])

  return !isSmallScreen ? (
    <>
      <Autocomplete
        autoHighlight
        key={`${name}${value}`}
        sx={{
          '& .MuiFormControl-root': {
            height: theme.spacing(8),
          },
          maxWidth: '100%',
          '& .MuiInputLabel-root': {
            maxWidth: 'calc(100% - 40px)',
            minWidth: 'calc(100% - 40px)',
          },
          '& .MuiInputLabel-formControl': {
            transform: value ? 'translate(0, 12px) scale(0.857)' : '',
          },
          ...(disablePortal && {
            '& + .MuiAutocomplete-popperDisablePortal': {
              position: 'initial',
            },
          }),
          ...(readOnly && {
            '& .MuiFormLabel-root ': {
              zIndex: 1,
            },
            '& .MuiInputBase-root': {
              margin: '0px!important',
              backgroundColor: '#F5F5F5',
              pointerEvents: 'none',
              color: '#999999',
            },
            '& .MuiInput-root': {
              margin: '0 !important',
            },
            '& .MuiIconButton-root': {
              color: '#999999',
            },
          }),
          '& .MuiAutocomplete-endAdornment': {
            top: readOnly ? theme.spacing(4) : theme.spacing(2),
          },
        }}
        slotProps={{
          paper: {
            sx: {
              '& .MuiAutocomplete-option[aria-selected="true"]': {
                position: 'relative',
                paddingRight: '40px',
                backgroundColor: 'transparent !important',
                ...(!withCheckbox && {
                  '&::after': {
                    position: 'absolute',
                    top: 12,
                    right: 16,
                    content: '""',
                    width: 14,
                    height: 8,
                    borderLeft: '2px solid',
                    borderBottom: '2px solid',
                    transform: 'rotate(-45deg)',
                  },
                }),
              },
              '& .MuiAutocomplete-option[data-focus="true"]': {
                color: '#434343',
                backgroundColor: `${theme.palette.action.hover} !important`,
              },
            },
          },
        }}
        disableClearable={disableClearable}
        disableCloseOnSelect
        options={data}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        isOptionEqualToValue={(option, value) => option.key === value.key}
        getOptionLabel={(option) => option?.label || ''}
        getOptionDisabled={(option) => option?.disabled || false}
        onChange={(_, option) => {
          handleChange(option?.key ?? null)
          handleClose()
        }}
        value={selectedOption}
        disabled={readOnly}
        renderInput={(params) => (
          <StyledTextField
            variant="standard"
            label={label}
            margin="none"
            isLongLabel={isLongLabel}
            name={name}
            {...params}
            error={!!error}
            helperText={error ? error.message : null}
            disabled={readOnly}
            onKeyDown={(e) => onlyLatinLetters && handleLatinKeysDown(e, onlyLatinLetters)}
            {...rest}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {endAdornmentIcon && <Box mr={1}>{endAdornmentIcon}</Box>}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
              htmlInput: {
                ...rest.inputProps,
                ...params.inputProps,
                readOnly,
                'data-test': `autotest-${name}`,
              },
            }}
          />
        )}
        filterOptions={filterOption}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            {withCheckbox ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                width="100%"
                sx={{
                  '&:hover': {
                    '& svg > rect': {
                      stroke: '#000000',
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    '& .MuiCheckbox-root': {
                      padding: theme.spacing(0.5, 0.5, 0.5, 0.5),
                      margin: theme.spacing(0.7, 2, 0.7, 0),
                    },
                    '& .MuiIconButton-colorSecondary': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <StyledCheckbox checked={selected} />
                </Box>
                {option.label}
              </Box>
            ) : (
              <Box style={option.style || {}}>{option.label}</Box>
            )}
          </li>
        )}
        disablePortal={disablePortal}
        popupIcon={<ExpandMoreIcon />}
        openOnFocus={!readOnly}
        blurOnSelect={false}
      />
    </>
  ) : (
    <MobileSelector
      name={name}
      label={label}
      options={data as OptionType[]}
      handleChange={handleChange}
      readOnly={readOnly}
      doubleRows={doubleRows}
      isDynamicLabels
      {...omit(rest, 'variant')}
    />
  )
}

export const FormAutocompleteSelect = React.memo(FormAutocompleteSelectComponent, isEqual)
